<section class="hero is-primary is-fullheight-with-navbar">
  <div class="hero-body">
    <div class="content-wrapper has-text-centered">
      <div class="columns is-vcentered">
        <div class="column">
          <h1 class="title is-1 has-text-weight-bold has-text-white">
            Rekavoze — Smart Inventory & Expense Management for SMBs
          </h1>
          <p class="subtitle is-4 mt-3 has-text-white">
            Inventory & Expense Management for Small and Growing Businesses
          </p>
          <div class="buttons is-centered mt-5">
            <a
              href="https://rekavoze.com"
              target="_blank"
              class="button is-light is-medium"
            >
              See it in Action
            </a>
            <a
              href="https://app.rekavoze.com/register"
              target="_blank"
              class="button is-white is-outlined is-medium"
              >Start Free Trial</a
            >
          </div>
        </div>
        <div class="column">
          <figure class="image mt-6 illustration">
            <img
              src="/assets/products/rekavoze/inventory-logistics.svg"
              alt="Inventory Logistics Illustration"
            />
          </figure>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- PROBLEM + SOLUTION -->
<section class="section">
  <div class="container">
    <div class="columns is-vcentered">
      <div class="column is-half">
        <h2 class="title is-3">Stop Juggling Spreadsheets</h2>
        <p class="subtitle is-5">
          Manual tracking leads to errors, stockouts, overspending, and missed
          insights. SMBs need
          <em>clarity</em> — not chaos.
        </p>
        <p>
          <strong>Rekavoze</strong> brings everything into one system: real-time
          stock tracking, expense categorization, profit analytics, and
          automated insights — all built for small businesses and entrepreneurs.
        </p>
      </div>
      <div class="column is-half has-text-centered">
        <picture>
          <source
            srcset="/assets/products/rekavoze/dashboard-dark.avif"
            media="(prefers-color-scheme: dark)"
          />
          <img
            src="/assets/products/rekavoze/dashboard.avif"
            alt="Rekavoze Dashboard Preview"
            width="1920"
            height="1080"
            loading="lazy"
          />
        </picture>
      </div>
    </div>
  </div>
</section>

<!-- FEATURES -->
<section class="section has-background-light">
  <div class="container">
    <h2 class="title is-3 has-text-centered">Built for Modern SMBs</h2>
    <div class="columns is-multiline mt-5">
      <!-- Inventory Mastery -->
      <div class="column is-half">
        <div class="media">
          <div class="media-left">
            <fa-icon class="icon feature-icon" [icon]="icons.inventory" />
          </div>
          <div class="media-content">
            <p class="title is-5">Inventory Mastery</p>
            <p>
              Track raw materials to finished goods with SKU-level precision.
              Never lose a unit again.
            </p>
          </div>
        </div>
      </div>
      <!-- Expense Intelligence -->
      <div class="column is-half">
        <div class="media">
          <div class="media-left">
            <fa-icon class="icon feature-icon" [icon]="icons.expense" />
          </div>
          <div class="media-content">
            <p class="title is-5">Expense Intelligence</p>
            <p>
              Categorize every expense automatically and see where your money
              goes with one-click reports.
            </p>
          </div>
        </div>
      </div>
      <!-- Profit Analytics -->
      <div class="column is-half">
        <div class="media">
          <div class="media-left">
            <fa-icon class="icon feature-icon" [icon]="icons.analytics" />
          </div>
          <div class="media-content">
            <p class="title is-5">Profit Analytics</p>
            <p>
              View real-time dashboards showing COGS, revenue, and net margins
              across channels.
            </p>
          </div>
        </div>
      </div>
      <!-- Audit Logging -->
      <div class="column is-half">
        <div class="media">
          <div class="media-left">
            <fa-icon class="icon feature-icon" [icon]="icons.audit" />
          </div>
          <div class="media-content">
            <p class="title is-5">Audit Logging</p>
            <p>
              Stay compliant and secure with a full history of every change and
              export log.
            </p>
          </div>
        </div>
      </div>
      <!-- Budget Control -->
      <div class="column is-half">
        <div class="media">
          <div class="media-left">
            <fa-icon class="icon feature-icon" [icon]="icons.budget" />
          </div>
          <div class="media-content">
            <p class="title is-5">Budget Control</p>
            <p>
              Set departmental limits and get alerts before you exceed your
              runway.
            </p>
          </div>
        </div>
      </div>
      <!-- Bulk Import -->
      <div class="column is-half">
        <div class="media">
          <div class="media-left">
            <fa-icon class="icon feature-icon" [icon]="icons.io" />
          </div>
          <div class="media-content">
            <p class="title is-5">Bulk Operations</p>
            <p>
              Import thousands of items or transactions instantly with smart CSV
              mapping.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- PRICING -->
<section class="section">
  <div class="container has-text-centered">
    <h2 class="title is-3">Simple and Transparent Pricing</h2>
    <div class="columns is-centered mt-5">
      <!-- Starter -->
      <div class="column is-one-quarter price-card">
        <p class="title is-4">Starter</p>
        <p>Free</p>
        <hr />
        <ul class="has-text-left">
          <li>Up to 30 Items</li>
          <li>Single Business Access</li>
          <li>Basic Reports</li>
          <li>WhatsApp Support</li>
        </ul>
        <a
          href="https://app.rekavoze.com/register"
          target="_blank"
          class="button is-primary is-fullwidth mt-4"
          >Start Free</a
        >
      </div>
      <!-- Growth -->
      <div class="column is-one-quarter price-card">
        <p class="title is-4">Growth</p>
        <p><strong>₹999/mo</strong></p>
        <hr />
        <ul class="has-text-left">
          <li>Unlimited Items</li>
          <li>Multi-User Access</li>
          <li>Advanced Analytics</li>
          <li>Full Audit History</li>
        </ul>
        <a
          href="https://app.rekavoze.com/register"
          target="_blank"
          class="button is-primary is-fullwidth mt-4"
          >Go Pro</a
        >
      </div>
      <!-- Scale -->
      <div class="column is-one-quarter price-card">
        <p class="title is-4">Scale</p>
        <p><strong>₹2,499/mo</strong></p>
        <hr />
        <ul class="has-text-left">
          <li>Multi-Business Support</li>
          <li>API Integrations</li>
          <li>Priority Onboarding</li>
          <li>Custom Reports</li>
        </ul>
        <a
          href="https://wa.me/919823994536?text=Hi,%20I%27m%20interested%20in%20purchasing%20Rekavoze%20at%20Scale%20for%20my%20business.%20Can%20you%20help%20me%20with%20the%20purchasing%20process?"
          target="_blank"
          class="button is-primary is-fullwidth mt-4"
          >Contact Sales</a
        >
      </div>
    </div>
  </div>
</section>

<!-- FINAL CTA -->
<section class="section has-background-light">
  <div class="container has-text-centered">
    <h2 class="title is-3">Get Clear. Stay in Control.</h2>
    <p class="subtitle is-5">
      Stop second-guessing your inventory and let Rekavoze handle the numbers
      for you.
    </p>
    <a
      href="https://app.rekavoze.com/register"
      target="_blank"
      class="button is-primary is-large"
      >Start Your Free Trial</a
    >
  </div>
</section>
